import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { ZoneType, DensityType, BuildingState } from '../simulation/types';

export class AssetManager {
  private static instance: AssetManager | null = null;
  private gltfLoader: GLTFLoader;
  private loadedModels: Map<string, THREE.Group> = new Map();
  private meshCache: Map<string, THREE.Object3D> = new Map();

  // Shared materials for performance
  private materials: Record<string, THREE.Material> = {};

  private constructor() {
    this.gltfLoader = new GLTFLoader();
    this.initMaterials();
  }

  public static getInstance(): AssetManager {
    if (!AssetManager.instance) {
      AssetManager.instance = new AssetManager();
    }
    return AssetManager.instance;
  }

  private initMaterials() {
    this.materials = {
      grass: new THREE.MeshStandardMaterial({ color: 0x7cb342, roughness: 0.6, metalness: 0.1 }),
      water: new THREE.MeshStandardMaterial({ color: 0x0288d1, roughness: 0.1, metalness: 0.8, transparent: true, opacity: 0.85 }),
      sand: new THREE.MeshStandardMaterial({ color: 0xfbc02d, roughness: 0.8, metalness: 0.05 }),
      rock: new THREE.MeshStandardMaterial({ color: 0x616161, roughness: 0.7, metalness: 0.2 }),
      
      asphalt: new THREE.MeshStandardMaterial({ color: 0x263238, roughness: 0.8, metalness: 0.1 }),
      roadMarkingYellow: new THREE.MeshStandardMaterial({ color: 0xffeb3b, roughness: 0.9, metalness: 0.0 }),
      roadMarkingWhite: new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9, metalness: 0.0 }),
      curb: new THREE.MeshStandardMaterial({ color: 0xb0bec5, roughness: 0.7, metalness: 0.1 }),
      
      trunk: new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.9, metalness: 0.0 }),
      leavesOak: new THREE.MeshStandardMaterial({ color: 0x33691e, roughness: 0.6, metalness: 0.1 }),
      leavesPine: new THREE.MeshStandardMaterial({ color: 0x1b5e20, roughness: 0.6, metalness: 0.1 }),
      leavesSakura: new THREE.MeshStandardMaterial({ color: 0xf48fb1, roughness: 0.6, metalness: 0.1 }),
      leavesPalm: new THREE.MeshStandardMaterial({ color: 0x2e7d32, roughness: 0.5, metalness: 0.1 }),

      buildingBase: new THREE.MeshStandardMaterial({ color: 0xeeeeee, roughness: 0.5, metalness: 0.1 }),
      buildingRoof: new THREE.MeshStandardMaterial({ color: 0xd32f2f, roughness: 0.5, metalness: 0.1 }),
      buildingGlass: new THREE.MeshStandardMaterial({ color: 0x80deea, roughness: 0.2, metalness: 0.9, emissive: 0x002c3c }),
      buildingGlassGlow: new THREE.MeshStandardMaterial({ color: 0xffe082, roughness: 0.3, metalness: 0.2, emissive: 0xffb300, emissiveIntensity: 1.2 }),
      scaffolding: new THREE.MeshStandardMaterial({ color: 0xffb300, roughness: 0.4, metalness: 0.5 }),
      concretePad: new THREE.MeshStandardMaterial({ color: 0x90a4ae, roughness: 0.8, metalness: 0.1 }),

      civicRed: new THREE.MeshStandardMaterial({ color: 0xc62828, roughness: 0.5, metalness: 0.1 }),
      civicBlue: new THREE.MeshStandardMaterial({ color: 0x1565c0, roughness: 0.5, metalness: 0.1 }),
      civicWhite: new THREE.MeshStandardMaterial({ color: 0xf5f5f5, roughness: 0.5, metalness: 0.1 }),
      civicBrick: new THREE.MeshStandardMaterial({ color: 0xa1887f, roughness: 0.8, metalness: 0.0 }),
      parkBench: new THREE.MeshStandardMaterial({ color: 0x3e2723, roughness: 0.8, metalness: 0.1 }),
    };
  }

  /**
   * Pre-loads GLTF models if available. Since we run in Vite, they can be served from /public/models/.
   * If assets fail to load, we log a warning and proceed with procedural generator.
   */
  public async preloadAssets(): Promise<void> {
    const modelsToLoad: string[] = [
      // Potential model keys, e.g., 'car', 'tree', 'building_res_low'
    ];

    const promises = modelsToLoad.map(async (key) => {
      try {
        const gltf = await this.gltfLoader.loadAsync(`/assets/models/${key}.glb`);
        this.loadedModels.set(key, gltf.scene);
        console.log(`[AssetManager] Loaded model: ${key}`);
      } catch (err) {
        console.warn(`[AssetManager] Failed to load model "${key}". Procedural fallbacks will be used.`, err);
      }
    });

    await Promise.all(promises);
  }

  /**
   * Modifies the shared glass material's emissive intensity for cozy nighttime glows
   */
  public setNocturnalGlow(intensity: number) {
    const glass = this.materials.buildingGlass as THREE.MeshStandardMaterial;
    if (glass) {
      glass.emissiveIntensity = intensity;
      // Soft warm amber-yellow glow at night, deep teal-blue during day
      glass.emissive.setHex(intensity > 0.1 ? 0xffb300 : 0x002c3c);
    }
  }

  /**
   * Returns a 3D block representation for terrain tiles.
   * fully volumetric (looks gorgeous from all sides)
   */
  public getTerrainMesh(type: number): THREE.Object3D {
    const cacheKey = `terrain_${type}`;
    if (this.meshCache.has(cacheKey)) {
      return this.meshCache.get(cacheKey)!.clone();
    }

    const group = new THREE.Group();
    
    // Choose dimensions and materials based on terrain type
    let material: THREE.Material = this.materials.grass;
    let height = 0.2;

    switch (type) {
      case 0: // water
        material = this.materials.water;
        height = 0.1;
        break;
      case 1: // sand
        material = this.materials.sand;
        height = 0.18;
        break;
      case 2: // grass
        material = this.materials.grass;
        height = 0.2;
        break;
      case 3: // rock
        material = this.materials.rock;
        height = 0.35;
        break;
    }

    // Top block
    const topGeo = new THREE.BoxGeometry(1.0, height, 1.0);
    const topMesh = new THREE.Mesh(topGeo, material);
    topMesh.position.y = height / 2;
    topMesh.castShadow = true;
    topMesh.receiveShadow = true;
    group.add(topMesh);

    // Soil/Base plate for a volumetric diorama look (extending downward)
    const baseGeo = new THREE.BoxGeometry(1.0, 0.4, 1.0);
    const baseMat = new THREE.MeshStandardMaterial({ color: 0x3e2723, roughness: 0.9, metalness: 0.1 }); // Rich dark brown soil
    const baseMesh = new THREE.Mesh(baseGeo, baseMat);
    baseMesh.position.y = -0.2;
    baseMesh.receiveShadow = true;
    group.add(baseMesh);

    this.meshCache.set(cacheKey, group);
    return group.clone();
  }

  /**
   * Generates a 3D vegetation mesh (Oak, Pine, Sakura, Palm)
   */
  public getVegetationMesh(type: 'oak' | 'pine' | 'sakura' | 'palm'): THREE.Object3D {
    const cacheKey = `tree_${type}`;
    if (this.meshCache.has(cacheKey)) {
      return this.meshCache.get(cacheKey)!.clone();
    }

    const group = new THREE.Group();

    // Trunk: Brown cylinder
    const trunkGeo = new THREE.CylinderGeometry(0.04, 0.06, 0.5, 5);
    const trunkMesh = new THREE.Mesh(trunkGeo, this.materials.trunk);
    trunkMesh.position.y = 0.25;
    trunkMesh.castShadow = true;
    trunkMesh.receiveShadow = true;
    group.add(trunkMesh);

    // Canopy
    if (type === 'oak') {
      const leavesGeo = new THREE.DodecahedronGeometry(0.2, 1);
      const leavesMesh = new THREE.Mesh(leavesGeo, this.materials.leavesOak);
      leavesMesh.position.y = 0.55;
      leavesMesh.castShadow = true;
      group.add(leavesMesh);

      // Add a couple of tiny branch clusters for 3D depth
      const leftGeo = new THREE.DodecahedronGeometry(0.12, 1);
      const leftMesh = new THREE.Mesh(leftGeo, this.materials.leavesOak);
      leftMesh.position.set(-0.1, 0.48, 0.05);
      leftMesh.castShadow = true;
      group.add(leftMesh);

      const rightGeo = new THREE.DodecahedronGeometry(0.11, 1);
      const rightMesh = new THREE.Mesh(rightGeo, this.materials.leavesOak);
      rightMesh.position.set(0.08, 0.5, -0.08);
      rightMesh.castShadow = true;
      group.add(rightMesh);

    } else if (type === 'pine') {
      // 3 overlapping green cones for a realistic low-poly look
      for (let i = 0; i < 3; i++) {
        const coneGeo = new THREE.ConeGeometry(0.18 - i * 0.04, 0.24, 5);
        const coneMesh = new THREE.Mesh(coneGeo, this.materials.leavesPine);
        coneMesh.position.y = 0.45 + i * 0.14;
        coneMesh.castShadow = true;
        group.add(coneMesh);
      }
    } else if (type === 'sakura') {
      const leavesGeo = new THREE.DodecahedronGeometry(0.22, 1);
      const leavesMesh = new THREE.Mesh(leavesGeo, this.materials.leavesSakura);
      leavesMesh.position.y = 0.55;
      leavesMesh.castShadow = true;
      group.add(leavesMesh);

      const smallGeo = new THREE.DodecahedronGeometry(0.14, 1);
      const smallMesh = new THREE.Mesh(smallGeo, this.materials.leavesSakura);
      smallMesh.position.set(0.1, 0.46, 0.08);
      smallMesh.castShadow = true;
      group.add(smallMesh);
    } else if (type === 'palm') {
      // Slanted trunk
      trunkMesh.rotation.z = 0.15;
      trunkMesh.rotation.x = -0.1;

      const leafGroup = new THREE.Group();
      leafGroup.position.set(-0.06, 0.5, 0.04);
      
      // Palm leaf planes rotated around the center
      for (let i = 0; i < 6; i++) {
        const angle = (i * Math.PI) / 3;
        const leafGeo = new THREE.BoxGeometry(0.22, 0.01, 0.06);
        const leafMesh = new THREE.Mesh(leafGeo, this.materials.leavesPalm);
        
        leafMesh.position.set(Math.cos(angle) * 0.1, -0.02, Math.sin(angle) * 0.1);
        leafMesh.rotation.y = -angle;
        leafMesh.rotation.z = 0.35; // droop down
        leafMesh.castShadow = true;
        
        leafGroup.add(leafMesh);
      }
      group.add(leafGroup);
    }

    // Scale down the whole tree a bit to fit nicely in 1x1 cells
    group.scale.set(0.8, 0.8, 0.8);

    this.meshCache.set(cacheKey, group);
    return group.clone();
  }

  /**
   * Generates a 3D road segment with sidewalk curb, asphalt base, and white/yellow line markings
   */
  public getRoadMesh(autoTilingId: string): THREE.Object3D {
    const cacheKey = `road_${autoTilingId}`;
    if (this.meshCache.has(cacheKey)) {
      return this.meshCache.get(cacheKey)!.clone();
    }

    const group = new THREE.Group();

    // Asphalt bed (slightly raised above terrain)
    const baseGeo = new THREE.BoxGeometry(1.0, 0.02, 1.0);
    const baseMesh = new THREE.Mesh(baseGeo, this.materials.asphalt);
    baseMesh.position.y = 0.01;
    baseMesh.receiveShadow = true;
    group.add(baseMesh);

    const mask = parseInt(autoTilingId.replace('road_tile_', ''), 10) || 0;

    // We can model detailed road markings based on the neighbor connection mask.
    // Let's create roads that look beautiful from 360°!
    // mask 10 = Straight horizontal (East-West)
    // mask 5  = Straight vertical (North-South)
    // To simplify: we will construct a single double-lane road base and apply markings.
    // For general aesthetic, let's add concrete sidewalk curbs on both sides or corners.

    // Let's model general elements:
    // Left & Right curbs (if horizontal straight, they are at Z = +/- 0.48)
    const curbHeight = 0.04;
    const curbThickness = 0.06;

    const addCurbX = (z: number) => {
      const curbGeo = new THREE.BoxGeometry(1.0, curbHeight, curbThickness);
      const curbMesh = new THREE.Mesh(curbGeo, this.materials.curb);
      curbMesh.position.set(0, curbHeight / 2, z);
      curbMesh.castShadow = true;
      curbMesh.receiveShadow = true;
      group.add(curbMesh);
    };

    const addCurbZ = (x: number) => {
      const curbGeo = new THREE.BoxGeometry(curbThickness, curbHeight, 1.0);
      const curbMesh = new THREE.Mesh(curbGeo, this.materials.curb);
      curbMesh.position.set(x, curbHeight / 2, 0);
      curbMesh.castShadow = true;
      curbMesh.receiveShadow = true;
      group.add(curbMesh);
    };

    // Draw lines
    const addYellowLineX = () => {
      const lineGeo = new THREE.BoxGeometry(1.0, 0.022, 0.02);
      const lineMesh = new THREE.Mesh(lineGeo, this.materials.roadMarkingYellow);
      lineMesh.position.set(0, 0.011, 0);
      group.add(lineMesh);
    };

    const addYellowLineZ = () => {
      const lineGeo = new THREE.BoxGeometry(0.02, 0.022, 1.0);
      const lineMesh = new THREE.Mesh(lineGeo, this.materials.roadMarkingYellow);
      lineMesh.position.set(0, 0.011, 0);
      group.add(lineMesh);
    };

    // Build specific layouts based on mask
    // We can group masks:
    // Straights: 5 (N-S), 10 (E-W)
    // Corners: 3 (NE), 6 (SE), 9 (NW), 12 (SW)
    // T-junctions: 7 (E-S-N), 11 (N-E-W), 13 (N-S-W), 14 (E-S-W)
    // Intersections: 15 (4-way)
    // Dead-ends / isolated: 0, 1, 2, 4, 8

    if (mask === 5 || mask === 1 || mask === 4 || mask === 0) {
      // North-South (or vertical dead ends)
      addCurbZ(-0.47);
      addCurbZ(0.47);
      addYellowLineZ();
    } else if (mask === 10 || mask === 2 || mask === 8) {
      // East-West (or horizontal dead ends)
      addCurbX(-0.47);
      addCurbX(0.47);
      addYellowLineX();
    } else if (mask === 15) {
      // 4-way intersection: curbs only in the 4 corners
      const cornerSize = 0.08;
      const makeCornerCurb = (cx: number, cz: number) => {
        const curbPart1 = new THREE.Mesh(new THREE.BoxGeometry(cornerSize, curbHeight, curbThickness), this.materials.curb);
        curbPart1.position.set(cx - Math.sign(cx)*curbThickness/2, curbHeight/2, cz);
        const curbPart2 = new THREE.Mesh(new THREE.BoxGeometry(curbThickness, curbHeight, cornerSize), this.materials.curb);
        curbPart2.position.set(cx, curbHeight/2, cz - Math.sign(cz)*curbThickness/2);
        group.add(curbPart1, curbPart2);
      };
      makeCornerCurb(0.47, 0.47);
      makeCornerCurb(-0.47, 0.47);
      makeCornerCurb(0.47, -0.47);
      makeCornerCurb(-0.47, -0.47);
    } else {
      // For any corner or T-junction, let's keep it simple: draw curbs on the outer edges and center markings.
      // E.g. default fallback: a cross marking style or circular roundabout-like look
      addCurbZ(-0.47);
      addCurbX(0.47);
      // Yellow dot in the center
      const dotGeo = new THREE.CylinderGeometry(0.06, 0.06, 0.022, 8);
      const dotMesh = new THREE.Mesh(dotGeo, this.materials.roadMarkingYellow);
      dotMesh.position.set(0, 0.011, 0);
      group.add(dotMesh);
    }

    this.meshCache.set(cacheKey, group);
    return group.clone();
  }

  /**
   * Generates a 3D construction stage representation
   */
  public getConstructionMesh(stage: BuildingState): THREE.Object3D {
    const group = new THREE.Group();

    // Flat concrete pad
    const padGeo = new THREE.BoxGeometry(0.8, 0.04, 0.8);
    const padMesh = new THREE.Mesh(padGeo, this.materials.concretePad);
    padMesh.position.y = 0.02;
    padMesh.castShadow = true;
    padMesh.receiveShadow = true;
    group.add(padMesh);

    if (stage === 'planned') {
      // Add 4 corner surveyor poles (thin brown cylinders)
      const poleGeo = new THREE.CylinderGeometry(0.01, 0.01, 0.25, 4);
      const poleMat = new THREE.MeshStandardMaterial({ color: 0xe65100, roughness: 0.6 }); // bright orange top
      
      const positions = [
        [-0.38, 0.38],
        [0.38, 0.38],
        [-0.38, -0.38],
        [0.38, -0.38]
      ];

      positions.forEach(([x, z]) => {
        const pole = new THREE.Mesh(poleGeo, poleMat);
        pole.position.set(x, 0.125 + 0.04, z);
        pole.castShadow = true;
        group.add(pole);
      });
    } else if (stage === 'foundation') {
      // Thicker concrete block with vertical steel rebar sticking out
      const slabGeo = new THREE.BoxGeometry(0.7, 0.08, 0.7);
      const slabMesh = new THREE.Mesh(slabGeo, this.materials.concretePad);
      slabMesh.position.y = 0.06;
      slabMesh.castShadow = true;
      group.add(slabMesh);

      // Rebar columns
      const rebarGeo = new THREE.CylinderGeometry(0.006, 0.006, 0.22, 4);
      const rebarMat = new THREE.MeshStandardMaterial({ color: 0x546e7a, metalness: 0.8, roughness: 0.5 });
      
      const rebarOffsets = [
        [-0.2, -0.2], [-0.2, 0.2], [0.2, -0.2], [0.2, 0.2],
        [0.0, -0.2], [0.0, 0.2], [-0.2, 0.0], [0.2, 0.0]
      ];

      rebarOffsets.forEach(([rx, rz]) => {
        const rebar = new THREE.Mesh(rebarGeo, rebarMat);
        rebar.position.set(rx, 0.11 + 0.08, rz);
        rebar.castShadow = true;
        group.add(rebar);
      });
    } else if (stage === 'construction') {
      // Scaffolding wooden cage enclosing a central brick-like block
      const brickGeo = new THREE.BoxGeometry(0.65, 0.4, 0.65);
      const brickMesh = new THREE.Mesh(brickGeo, this.materials.civicBrick);
      brickMesh.position.y = 0.2;
      brickMesh.castShadow = true;
      group.add(brickMesh);

      // Simple yellow frame struts representing scaffolding
      const strutGeo = new THREE.BoxGeometry(0.02, 0.48, 0.02);
      const makeStrut = (sx: number, sz: number) => {
        const strut = new THREE.Mesh(strutGeo, this.materials.scaffolding);
        strut.position.set(sx, 0.24, sz);
        strut.castShadow = true;
        group.add(strut);
      };

      // 4 corner struts
      makeStrut(0.35, 0.35);
      makeStrut(-0.35, 0.35);
      makeStrut(0.35, -0.35);
      makeStrut(-0.35, -0.35);

      // Horizontal scaffolding planks (wooden color)
      const plankMat = new THREE.MeshStandardMaterial({ color: 0x8d6e63, roughness: 0.8 });
      const plankGeoY1 = new THREE.BoxGeometry(0.74, 0.02, 0.08);
      const plank1 = new THREE.Mesh(plankGeoY1, plankMat);
      plank1.position.set(0, 0.25, 0.35);
      const plank2 = new THREE.Mesh(plankGeoY1, plankMat);
      plank2.position.set(0, 0.42, -0.35);
      group.add(plank1, plank2);
    } else if (stage === 'damaged') {
      // Scorched brick pile + flashing warning flare!
      const sootMat = new THREE.MeshStandardMaterial({ color: 0x212121, roughness: 0.9 }); // soot charcoal
      const fireBrick = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.16, 0.5), sootMat);
      fireBrick.position.y = 0.08 + 0.04;
      fireBrick.castShadow = true;
      group.add(fireBrick);

      // Warning siren cone on top (Z-centered)
      const beaconMat = new THREE.MeshBasicMaterial({ color: 0xff3d00 }); // Neon glowing red/orange
      const beacon = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.16, 6), beaconMat);
      beacon.position.set(0, 0.16 + 0.04 + 0.08, 0);
      group.add(beacon);
    } else if (stage === 'rubble') {
      // Completely destroyed pile of debris
      const debrisMat = new THREE.MeshStandardMaterial({ color: 0x424242, roughness: 0.8 }); // dark concrete debris
      const metalMat = new THREE.MeshStandardMaterial({ color: 0x78909c, metalness: 0.8 });

      // Spatters of debris blocks
      const positions = [
        [-0.15, 0.06, -0.1], [0.15, 0.05, 0.15],
        [-0.2, 0.04, 0.2], [0.18, 0.07, -0.18]
      ];
      positions.forEach(([dx, dy, dz], i) => {
        const block = new THREE.Mesh(new THREE.BoxGeometry(0.24 - i * 0.02, dy * 2, 0.18 + i * 0.02), debrisMat);
        block.position.set(dx, dy + 0.04, dz);
        block.rotation.set(Math.random() * 0.4, Math.random() * 3, Math.random() * 0.4);
        block.castShadow = true;
        group.add(block);
      });

      // Twisted rebar metal bar sticking out
      const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.3), metalMat);
      bar.position.set(-0.05, 0.12 + 0.04, -0.05);
      bar.rotation.set(0.6, 0.3, -0.4);
      bar.castShadow = true;
      group.add(bar);
    }

    return group;
  }

  /**
   * Generates a fully volumetric 360-degree building based on zone, density, level and variant
   */
  public getBuildingMesh(
    zone: ZoneType,
    density: DensityType,
    level: number,
    stage: BuildingState,
    assetVariant: number,
    familyId?: string
  ): THREE.Object3D {
    // Under-construction stages
    if (stage !== 'completed') {
      return this.getConstructionMesh(stage);
    }

    const cacheKey = `building_${zone}_${density}_L${level}_V${assetVariant}_${familyId || ''}`;
    if (this.meshCache.has(cacheKey)) {
      return this.meshCache.get(cacheKey)!.clone();
    }

    const group = new THREE.Group();

    // Base concrete slab for all completed buildings
    const slabGeo = new THREE.BoxGeometry(0.9, 0.04, 0.9);
    const slabMesh = new THREE.Mesh(slabGeo, this.materials.concretePad);
    slabMesh.position.y = 0.02;
    slabMesh.receiveShadow = true;
    group.add(slabMesh);

    // --- High-Fidelity Pocket City Utilities ---
    if (familyId === 'power_solar') {
      // 4 blue solar panel blocks arranged in 2x2
      const panelMat = new THREE.MeshStandardMaterial({ color: 0x01579b, roughness: 0.1, metalness: 0.9 });
      const supportMat = new THREE.MeshStandardMaterial({ color: 0x546e7a, roughness: 0.6 });
      
      const panelOffsets = [
        [-0.22, -0.22], [0.22, -0.22],
        [-0.22, 0.22], [0.22, 0.22]
      ];

      panelOffsets.forEach(([px, pz]) => {
        // support leg
        const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.1), supportMat);
        leg.position.set(px, 0.05 + 0.04, pz);
        leg.castShadow = true;
        group.add(leg);

        // solar plate tilted facing South (Z+)
        const plate = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.015, 0.28), panelMat);
        plate.position.set(px, 0.12 + 0.04, pz);
        plate.rotation.x = 0.35; // tilted forward
        plate.castShadow = true;
        group.add(plate);
      });

      // Central white generator cabin
      const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.2, 0.24), this.materials.civicWhite);
      cabin.position.set(0, 0.1 + 0.04, 0);
      cabin.castShadow = true;
      group.add(cabin);

      this.meshCache.set(cacheKey, group);
      return group.clone();
    }

    if (familyId === 'power_coal') {
      const sootMat = new THREE.MeshStandardMaterial({ color: 0x37474f, roughness: 0.9, metalness: 0.2 }); // dark grey
      const concrete = this.materials.concretePad;
      const pipeMat = new THREE.MeshStandardMaterial({ color: 0x78909c, roughness: 0.5 });

      // Main brick plant block
      const plant = new THREE.Mesh(new THREE.BoxGeometry(0.68, 0.35, 0.55), sootMat);
      plant.position.set(-0.06, 0.175 + 0.04, -0.05);
      plant.castShadow = true;
      group.add(plant);

      // Sloped roofs on plant
      const roof = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.06, 0.57), pipeMat);
      roof.position.set(-0.06, 0.36 + 0.04, -0.05);
      roof.rotation.z = 0.08;
      group.add(roof);

      // Giant smokestack chimney (cylinder)
      const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.12, 0.85, 10), concrete);
      stack.position.set(0.24, 0.425 + 0.04, 0.22);
      stack.castShadow = true;
      group.add(stack);

      // Red band on chimney top
      const band = new THREE.Mesh(new THREE.CylinderGeometry(0.095, 0.095, 0.08, 10), this.materials.civicRed);
      band.position.set(0.24, 0.8 + 0.04, 0.22);
      group.add(band);

      // Small details: tiny windows
      const windowMat = this.materials.buildingGlass;
      const win = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.1, 0.01), windowMat);
      win.position.set(-0.1, 0.25 + 0.04, 0.23);
      group.add(win);

      this.meshCache.set(cacheKey, group);
      return group.clone();
    }

    if (familyId === 'water_pump') {
      const pumpMat = new THREE.MeshStandardMaterial({ color: 0x01579b, roughness: 0.3, metalness: 0.7 }); // beautiful shiny dark blue
      const pipeMat = new THREE.MeshStandardMaterial({ color: 0xb0bec5, roughness: 0.4, metalness: 0.8 }); // silver pipes
      const brick = this.materials.civicWhite;

      // Small pump station control room
      const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.25, 0.4), brick);
      cabin.position.set(-0.18, 0.125 + 0.04, -0.18);
      cabin.castShadow = true;
      group.add(cabin);

      // Slanted blue roof on cabin
      const roof = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.04, 0.44), pumpMat);
      roof.position.set(-0.18, 0.26 + 0.04, -0.18);
      group.add(roof);

      // Large cylindrical pump motors (horizontal cylinders)
      const motor1 = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.32, 10), pumpMat);
      motor1.position.set(0.22, 0.12 + 0.04, 0.15);
      motor1.rotation.z = Math.PI / 2;
      motor1.castShadow = true;
      group.add(motor1);

      // Big piping layout looping into the floor
      const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.26, 8), pipeMat);
      pipe.position.set(0.22, 0.18 + 0.04, -0.05);
      pipe.rotation.x = Math.PI / 2;
      pipe.castShadow = true;
      group.add(pipe);

      this.meshCache.set(cacheKey, group);
      return group.clone();
    }

    if (familyId === 'water_tower') {
      const supportMat = new THREE.MeshStandardMaterial({ color: 0xb0bec5, roughness: 0.5, metalness: 0.6 });
      const tankMat = new THREE.MeshStandardMaterial({ color: 0x0288d1, roughness: 0.2, metalness: 0.8 });

      // 4 tall metallic pillars
      const pillarOffsets = [
        [-0.18, -0.18], [0.18, -0.18],
        [-0.18, 0.18], [0.18, 0.18]
      ];

      pillarOffsets.forEach(([px, pz]) => {
        const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.65), supportMat);
        pillar.position.set(px, 0.325 + 0.04, pz);
        pillar.castShadow = true;
        group.add(pillar);
      });

      // central water supply tube
      const centralPipe = new THREE.Mesh(new THREE.CylinderGeometry(0.024, 0.024, 0.65), supportMat);
      centralPipe.position.set(0, 0.325 + 0.04, 0);
      centralPipe.castShadow = true;
      group.add(centralPipe);

      // Sphere blue tank on top
      const tank = new THREE.Mesh(new THREE.SphereGeometry(0.24, 12, 12), tankMat);
      tank.position.set(0, 0.68 + 0.04, 0);
      tank.scale.set(1.0, 1.1, 1.0); // oval look
      tank.castShadow = true;
      group.add(tank);

      this.meshCache.set(cacheKey, group);
      return group.clone();
    }

    // --- High-Fidelity Pocket City Grand Projects ---
    if (familyId === 'stadium') {
      const concrete = this.materials.civicWhite;
      const grassMat = this.materials.grass;

      // Inner green turf
      const turf = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.01, 0.65), grassMat);
      turf.position.set(0, 0.021, 0);
      group.add(turf);

      // Circular stands
      const makeStand = (sx: number, sz: number, sw: number, sd: number, sh: number, rx = 0) => {
        const stand = new THREE.Mesh(new THREE.BoxGeometry(sw, sh, sd), concrete);
        stand.position.set(sx, sh/2 + 0.02, sz);
        stand.rotation.y = rx;
        stand.castShadow = true;
        group.add(stand);
      };

      makeStand(0, 0.38, 0.8, 0.12, 0.22); // South
      makeStand(0, -0.38, 0.8, 0.12, 0.22); // North
      makeStand(0.32, 0, 0.12, 0.66, 0.22); // East
      makeStand(-0.32, 0, 0.12, 0.66, 0.22); // West

      this.meshCache.set(cacheKey, group);
      return group.clone();
    }

    if (familyId === 'panoramic_tower') {
      const steelMat = this.materials.concretePad;
      const glassMat = this.materials.buildingGlass;

      // Pedestal
      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.35, 0.08, 12), steelMat);
      base.position.set(0, 0.04, 0);
      base.castShadow = true;
      group.add(base);

      // Spire
      const spire = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.045, 1.2, 8), steelMat);
      spire.position.set(0, 0.6 + 0.04, 0);
      spire.castShadow = true;
      group.add(spire);

      // Observation deck
      const deck = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.18, 0.14, 12), glassMat);
      deck.position.set(0, 1.05 + 0.04, 0);
      deck.castShadow = true;
      
      const roof = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, 0.04, 12), steelMat);
      roof.position.set(0, 1.14 + 0.04, 0);
      group.add(deck, roof);

      // Spire antenna
      const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.01, 0.25, 4), steelMat);
      antenna.position.set(0, 1.28 + 0.04, 0);
      group.add(antenna);

      this.meshCache.set(cacheKey, group);
      return group.clone();
    }

    if (familyId === 'central_station') {
      const brickMat = this.materials.civicBrick;
      
      // Platform
      const platform = new THREE.Mesh(new THREE.BoxGeometry(0.88, 0.05, 0.45), this.materials.concretePad);
      platform.position.set(0, 0.025, 0.15);
      group.add(platform);

      // Tracks
      const track = new THREE.Mesh(new THREE.BoxGeometry(0.88, 0.01, 0.22), this.materials.asphalt);
      track.position.set(0, 0.025, -0.25);
      group.add(track);

      // Station building
      const hall = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.32, 0.32), brickMat);
      hall.position.set(0, 0.16 + 0.04, 0.2);
      hall.castShadow = true;
      group.add(hall);

      // Clock arch
      const arch = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.33, 8), this.materials.civicWhite);
      arch.position.set(0, 0.34, 0.2);
      arch.rotation.x = Math.PI / 2;
      group.add(arch);

      this.meshCache.set(cacheKey, group);
      return group.clone();
    }

    if (familyId === 'airport') {
      const terminalMat = this.materials.civicWhite;
      const runwayMat = this.materials.asphalt;

      // Runway
      const runway = new THREE.Mesh(new THREE.BoxGeometry(0.88, 0.01, 0.25), runwayMat);
      runway.position.set(0, 0.025, 0.3);
      group.add(runway);

      // White stripe
      const line = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.012, 0.02), this.materials.roadMarkingWhite);
      line.position.set(0, 0.031, 0.3);
      group.add(line);

      // Terminal
      const terminal = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.2, 0.4), terminalMat);
      terminal.position.set(-0.08, 0.12, -0.1);
      terminal.castShadow = true;
      group.add(terminal);

      // Tower
      const tower = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 0.4), terminalMat);
      tower.position.set(0.25, 0.22, -0.1);
      tower.castShadow = true;
      
      const cab = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.05, 0.08, 6), this.materials.buildingGlass);
      cab.position.set(0.25, 0.44, -0.1);
      cab.castShadow = true;
      group.add(tower, cab);

      this.meshCache.set(cacheKey, group);
      return group.clone();
    }

    if (familyId === 'port') {
      const wallMat = this.materials.buildingBase;
      const waterMat = this.materials.water;
      const craneMat = this.materials.scaffolding;

      // Water
      const water = new THREE.Mesh(new THREE.BoxGeometry(0.88, 0.02, 0.3), waterMat);
      water.position.set(0, 0.03, 0.28);
      group.add(water);

      // Platform
      const dock = new THREE.Mesh(new THREE.BoxGeometry(0.88, 0.04, 0.58), this.materials.concretePad);
      dock.position.set(0, 0.02, -0.16);
      dock.receiveShadow = true;
      group.add(dock);

      // Warehouse
      const warehouse = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.22, 0.35), wallMat);
      warehouse.position.set(-0.1, 0.13, -0.2);
      warehouse.castShadow = true;
      group.add(warehouse);

      // Crane
      const craneLeg = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.25), craneMat);
      craneLeg.position.set(0.24, 0.145, 0.12);
      const craneArm = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.015, 0.015), craneMat);
      craneArm.position.set(0.18, 0.27, 0.12);
      group.add(craneLeg, craneArm);

      this.meshCache.set(cacheKey, group);
      return group.clone();
    }

    // Color theme helper
    const getVariantColors = (index: number) => {
      const themes = [
        { wall: 0xfff9c4, trim: 0xf57f17 }, // Yellow-orange
        { wall: 0xe0f7fa, trim: 0x006064 }, // Teal-cyan
        { wall: 0xf3e5f5, trim: 0x4a148c }, // Lavender-purple
        { wall: 0xffebeb, trim: 0xb71c1c }, // Rose-brick
        { wall: 0xe8f5e9, trim: 0x1b5e20 }, // Mint-green
      ];
      return themes[index % themes.length];
    };

    const colors = getVariantColors(assetVariant);
    const wallMat = new THREE.MeshStandardMaterial({ color: colors.wall, roughness: 0.6, metalness: 0.1 });
    const trimMat = new THREE.MeshStandardMaterial({ color: colors.trim, roughness: 0.4, metalness: 0.2 });

    // Build models based on category and level
    if (zone === 'residential') {
      if (level <= 2) {
        // --- Low Density: Cute Suburban Villa ---
        const wallGeo = new THREE.BoxGeometry(0.7, 0.3, 0.7);
        const wallMesh = new THREE.Mesh(wallGeo, wallMat);
        wallMesh.position.y = 0.15 + 0.04;
        wallMesh.castShadow = true;
        wallMesh.receiveShadow = true;
        group.add(wallMesh);

        // Slanted terracotta roof (Wedge/Cone style)
        // A cone with 4 segments creates a perfect low-poly pyramid roof!
        const roofGeo = new THREE.ConeGeometry(0.58, 0.25, 4);
        const roofMesh = new THREE.Mesh(roofGeo, this.materials.buildingRoof);
        roofMesh.position.y = 0.3 + 0.125 + 0.04;
        roofMesh.rotation.y = Math.PI / 4; // Align pyramid face
        roofMesh.castShadow = true;
        group.add(roofMesh);

        // Cozy front door (Y=0.04 to 0.18, Facing South/Z-positive)
        const doorGeo = new THREE.BoxGeometry(0.12, 0.18, 0.02);
        const doorMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.8 });
        const door = new THREE.Mesh(doorGeo, doorMat);
        door.position.set(0, 0.09 + 0.04, 0.35);
        door.castShadow = true;
        group.add(door);

        // Small glowing windows on all 4 faces
        const makeWindow = (wx: number, wy: number, wz: number, rx = 0, ry = 0) => {
          const winGeo = new THREE.BoxGeometry(0.1, 0.1, 0.01);
          const win = new THREE.Mesh(winGeo, this.materials.buildingGlass);
          win.position.set(wx, wy, wz);
          win.rotation.set(rx, ry, 0);
          group.add(win);
        };
        makeWindow(-0.2, 0.22, 0.35); // front left
        makeWindow(0.2, 0.22, 0.35);  // front right
        makeWindow(-0.35, 0.22, 0.0, 0, Math.PI/2); // left
        makeWindow(0.35, 0.22, 0.0, 0, Math.PI/2);  // right
        makeWindow(0.0, 0.22, -0.35); // back center

      } else if (level <= 4) {
        // --- Medium Density: Elegant Multistory Apartment Flats ---
        // Stacked structure: 2 storeys
        for (let storey = 0; storey < 2; storey++) {
          const sy = 0.04 + storey * 0.3;
          
          const floorGeo = new THREE.BoxGeometry(0.74, 0.26, 0.74);
          const floorMesh = new THREE.Mesh(floorGeo, wallMat);
          floorMesh.position.y = sy + 0.13;
          floorMesh.castShadow = true;
          floorMesh.receiveShadow = true;
          group.add(floorMesh);

          // Slab separator trim
          const trimGeo = new THREE.BoxGeometry(0.78, 0.04, 0.78);
          const trimMesh = new THREE.Mesh(trimGeo, trimMat);
          trimMesh.position.y = sy + 0.26 + 0.02;
          trimMesh.castShadow = true;
          group.add(trimMesh);

          // Add windows to all sides of each floor
          const makeWin = (wx: number, wy: number, wz: number, rot = 0) => {
            const win = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.14, 0.01), this.materials.buildingGlass);
            win.position.set(wx, wy, wz);
            win.rotation.y = rot;
            group.add(win);
          };

          const wy = sy + 0.13;
          makeWin(-0.18, wy, 0.37);
          makeWin(0.18, wy, 0.37);
          makeWin(-0.18, wy, -0.37);
          makeWin(0.18, wy, -0.37);
          makeWin(-0.37, wy, -0.15, Math.PI/2);
          makeWin(-0.37, wy, 0.15, Math.PI/2);
          makeWin(0.37, wy, -0.15, Math.PI/2);
          makeWin(0.37, wy, 0.15, Math.PI/2);
        }

        // Flat roof detail: a cozy brown skylight dome or brick chimney
        const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.04, 0.72), trimMat);
        roofSlab.position.y = 0.04 + 0.6 + 0.02;
        group.add(roofSlab);

        const acGeo = new THREE.BoxGeometry(0.12, 0.1, 0.12);
        const acMesh = new THREE.Mesh(acGeo, new THREE.MeshStandardMaterial({ color: 0x90a4ae, roughness: 0.5 }));
        acMesh.position.set(0.12, 0.04 + 0.6 + 0.04 + 0.05, -0.12);
        acMesh.castShadow = true;
        group.add(acMesh);

      } else {
        // --- High Density: Majestic Residential High-Rise Tower ---
        // Tall skyscraper with 5 repeating glass floors and a beautiful antenna on top
        const towerHeight = 1.3;
        const towerGeo = new THREE.BoxGeometry(0.64, towerHeight, 0.64);
        const towerMesh = new THREE.Mesh(towerGeo, wallMat);
        towerMesh.position.y = towerHeight / 2 + 0.04;
        towerMesh.castShadow = true;
        towerMesh.receiveShadow = true;
        group.add(towerMesh);

        // Sleek corner architectural columns
        const colGeo = new THREE.BoxGeometry(0.06, towerHeight, 0.06);
        const makeCol = (cx: number, cz: number) => {
          const col = new THREE.Mesh(colGeo, trimMat);
          col.position.set(cx, towerHeight/2 + 0.04, cz);
          col.castShadow = true;
          group.add(col);
        };
        makeCol(0.31, 0.31);
        makeCol(-0.31, 0.31);
        makeCol(0.31, -0.31);
        makeCol(-0.31, -0.31);

        // Regular grid of beautiful glass curtain windows
        const makeGlassBand = (wy: number) => {
          const bandGeo = new THREE.BoxGeometry(0.54, 0.08, 0.65);
          const band = new THREE.Mesh(bandGeo, this.materials.buildingGlass);
          band.position.set(0, wy, 0);
          group.add(band);

          const bandGeoZ = new THREE.BoxGeometry(0.65, 0.08, 0.54);
          const bandZ = new THREE.Mesh(bandGeoZ, this.materials.buildingGlass);
          bandZ.position.set(0, wy, 0);
          group.add(bandZ);
        };

        for (let f = 0; f < 5; f++) {
          makeGlassBand(0.15 + f * 0.24 + 0.04);
        }

        // Rooftop antenna spire
        const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.05, 0.56), trimMat);
        roofSlab.position.y = towerHeight + 0.04 + 0.025;
        group.add(roofSlab);

        const spireGeo = new THREE.CylinderGeometry(0.008, 0.016, 0.3, 4);
        const spire = new THREE.Mesh(spireGeo, new THREE.MeshStandardMaterial({ color: 0xb0bec5, metalness: 0.9, roughness: 0.2 }));
        spire.position.set(0, towerHeight + 0.04 + 0.05 + 0.15, 0);
        spire.castShadow = true;
        group.add(spire);

        // Blinking red beacon light at the very tip!
        const beaconGeo = new THREE.SphereGeometry(0.02, 6, 6);
        const beaconMat = new THREE.MeshBasicMaterial({ color: 0xff1744 });
        const beacon = new THREE.Mesh(beaconGeo, beaconMat);
        beacon.position.set(0, towerHeight + 0.04 + 0.05 + 0.3, 0);
        group.add(beacon);
      }
    } 
    else if (zone === 'commercial') {
      if (level <= 2) {
        // --- Low Density: Cozy Street Corner Store ---
        const shopGeo = new THREE.BoxGeometry(0.74, 0.35, 0.74);
        const shopMesh = new THREE.Mesh(shopGeo, wallMat);
        shopMesh.position.y = 0.175 + 0.04;
        shopMesh.castShadow = true;
        shopMesh.receiveShadow = true;
        group.add(shopMesh);

        // Gorgeous 360-degree striped awnings!
        // Red-and-white awning on the front facade
        const awningMat = new THREE.MeshStandardMaterial({ color: 0xe53935, roughness: 0.5 });
        const awningGeo = new THREE.BoxGeometry(0.68, 0.04, 0.18);
        const awning = new THREE.Mesh(awningGeo, awningMat);
        awning.position.set(0, 0.26 + 0.04, 0.36);
        awning.rotation.x = 0.3; // slanted down
        awning.castShadow = true;
        group.add(awning);

        // Back/Side awning in matching styled white/red striped pattern (represented by submesh)
        const awning2 = new THREE.Mesh(awningGeo, awningMat);
        awning2.position.set(0, 0.26 + 0.04, -0.36);
        awning2.rotation.x = -0.3;
        awning2.castShadow = true;
        group.add(awning2);

        // Huge storefront glass panes (front/back display windows)
        const windowGeo = new THREE.BoxGeometry(0.24, 0.18, 0.02);
        const winLeft = new THREE.Mesh(windowGeo, this.materials.buildingGlass);
        winLeft.position.set(-0.18, 0.14 + 0.04, 0.38);
        const winRight = new THREE.Mesh(windowGeo, this.materials.buildingGlass);
        winRight.position.set(0.18, 0.14 + 0.04, 0.38);
        group.add(winLeft, winRight);

      } else if (level <= 4) {
        // --- Medium Density: Modern Shopping Arcade ---
        const blockGeo = new THREE.BoxGeometry(0.8, 0.55, 0.8);
        const blockMesh = new THREE.Mesh(blockGeo, wallMat);
        blockMesh.position.y = 0.275 + 0.04;
        blockMesh.castShadow = true;
        blockMesh.receiveShadow = true;
        group.add(blockMesh);

        // Sleek diagonal trim
        const bandGeo = new THREE.BoxGeometry(0.84, 0.08, 0.84);
        const band = new THREE.Mesh(bandGeo, trimMat);
        band.position.y = 0.36 + 0.04;
        band.castShadow = true;
        group.add(band);

        // Front glass curtain entrance with side display panels
        const entryGeo = new THREE.BoxGeometry(0.5, 0.28, 0.02);
        const entry = new THREE.Mesh(entryGeo, this.materials.buildingGlass);
        entry.position.set(0, 0.14 + 0.04, 0.41);
        group.add(entry);

        const sideGlass = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.35, 0.5), this.materials.buildingGlass);
        sideGlass.position.set(0.41, 0.2 + 0.04, 0);
        const sideGlassL = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.35, 0.5), this.materials.buildingGlass);
        sideGlassL.position.set(-0.41, 0.2 + 0.04, 0);
        group.add(sideGlass, sideGlassL);

      } else {
        // --- High Density: Commercial Office / Mall Spire ---
        const towerHeight = 1.4;
        const towerGeo = new THREE.BoxGeometry(0.6, towerHeight, 0.6);
        const towerMesh = new THREE.Mesh(towerGeo, this.materials.buildingGlass); // Primary glass tower
        towerMesh.position.y = towerHeight / 2 + 0.04;
        towerMesh.castShadow = true;
        towerMesh.receiveShadow = true;
        group.add(towerMesh);

        // Core concrete vertical pillars on the corners
        const pillarGeo = new THREE.BoxGeometry(0.08, towerHeight + 0.1, 0.08);
        const makePillar = (px: number, pz: number) => {
          const pillar = new THREE.Mesh(pillarGeo, trimMat);
          pillar.position.set(px, (towerHeight + 0.1)/2 + 0.04, pz);
          pillar.castShadow = true;
          group.add(pillar);
        };
        makePillar(0.31, 0.31);
        makePillar(-0.31, 0.31);
        makePillar(0.31, -0.31);
        makePillar(-0.31, -0.31);

        // Spans / Horizontal levels
        for (let storey = 0; storey < 4; storey++) {
          const sy = 0.04 + storey * 0.35;
          const bandGeo = new THREE.BoxGeometry(0.62, 0.04, 0.62);
          const band = new THREE.Mesh(bandGeo, wallMat);
          band.position.y = sy;
          group.add(band);
        }
      }
    } 
    else if (zone === 'office') {
      // --- Sleek High-Value Tech Spire ---
      const towerHeight = level <= 3 ? 0.9 : 1.5;
      const baseSize = level <= 3 ? 0.68 : 0.6;
      
      const mainGeo = new THREE.BoxGeometry(baseSize, towerHeight, baseSize);
      const mainMesh = new THREE.Mesh(mainGeo, this.materials.buildingGlass);
      mainMesh.position.y = towerHeight / 2 + 0.04;
      mainMesh.castShadow = true;
      mainMesh.receiveShadow = true;
      group.add(mainMesh);

      // Distinct corporate frame wrapper around the glass cube
      const frameMat = new THREE.MeshStandardMaterial({ color: 0x37474f, metalness: 0.8, roughness: 0.2 });
      
      const frameLGeo = new THREE.BoxGeometry(0.04, towerHeight + 0.04, baseSize + 0.04);
      const frameL = new THREE.Mesh(frameLGeo, frameMat);
      frameL.position.set(-baseSize/2 - 0.01, (towerHeight + 0.04)/2 + 0.02, 0);
      frameL.castShadow = true;
      
      const frameR = frameL.clone();
      frameR.position.x = baseSize/2 + 0.01;
      group.add(frameL, frameR);

      // Spire top for high levels
      if (level > 3) {
        const hpadGeo = new THREE.CylinderGeometry(0.24, 0.24, 0.04, 16);
        const hpadMat = new THREE.MeshStandardMaterial({ color: 0x78909c, roughness: 0.6 });
        const hpad = new THREE.Mesh(hpadGeo, hpadMat);
        hpad.position.set(0, towerHeight + 0.04 + 0.02, 0);
        hpad.castShadow = true;
        group.add(hpad);

        // Big white 'H' marking on helipad
        const hMark1 = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.042, 0.16), this.materials.roadMarkingWhite);
        hMark1.position.set(-0.06, towerHeight + 0.04 + 0.041, 0);
        const hMark2 = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.042, 0.16), this.materials.roadMarkingWhite);
        hMark2.position.set(0.06, towerHeight + 0.04 + 0.041, 0);
        const hMark3 = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.042, 0.04), this.materials.roadMarkingWhite);
        hMark3.position.set(0, towerHeight + 0.04 + 0.041, 0);
        group.add(hMark1, hMark2, hMark3);
      }
    } 
    else if (zone === 'industrial') {
      if (level <= 2) {
        // --- Low Density: Workshop/Garage ---
        const bGeo = new THREE.BoxGeometry(0.76, 0.28, 0.76);
        const bMesh = new THREE.Mesh(bGeo, this.materials.civicBrick);
        bMesh.position.y = 0.14 + 0.04;
        bMesh.castShadow = true;
        bMesh.receiveShadow = true;
        group.add(bMesh);

        // Slanted corrugated grey metal roof
        const roofMat = new THREE.MeshStandardMaterial({ color: 0x78909c, roughness: 0.4, metalness: 0.6 });
        const roofGeo = new THREE.BoxGeometry(0.82, 0.05, 0.82);
        const roof = new THREE.Mesh(roofGeo, roofMat);
        roof.position.set(0, 0.28 + 0.04, 0);
        roof.rotation.x = 0.12; // tilted slightly
        roof.castShadow = true;
        group.add(roof);

        // Large metallic roller door on the front face (Z+)
        const rollerMat = new THREE.MeshStandardMaterial({ color: 0xb0bec5, metalness: 0.8, roughness: 0.3 });
        const doorGeo = new THREE.BoxGeometry(0.3, 0.2, 0.02);
        const door = new THREE.Mesh(doorGeo, rollerMat);
        door.position.set(0, 0.1 + 0.04, 0.38);
        door.castShadow = true;
        group.add(door);

      } else if (level <= 4) {
        // --- Medium Density: Red-brick Factory ---
        const bGeo = new THREE.BoxGeometry(0.8, 0.36, 0.68);
        const bMesh = new THREE.Mesh(bGeo, this.materials.civicBrick);
        bMesh.position.y = 0.18 + 0.04;
        bMesh.castShadow = true;
        bMesh.receiveShadow = true;
        group.add(bMesh);

        // Red brick smokestack cylinder on the corner (360° beautiful detail)
        const stackGeo = new THREE.CylinderGeometry(0.06, 0.08, 0.65, 8);
        const stack = new THREE.Mesh(stackGeo, this.materials.civicBrick);
        stack.position.set(-0.28, 0.325 + 0.04, -0.2);
        stack.castShadow = true;
        group.add(stack);

        // Metal exhaust cap
        const capGeo = new THREE.CylinderGeometry(0.07, 0.07, 0.02, 8);
        const cap = new THREE.Mesh(capGeo, new THREE.MeshStandardMaterial({ color: 0x37474f, metalness: 0.7 }));
        cap.position.set(-0.28, 0.65 + 0.04 + 0.01, -0.2);
        group.add(cap);

        // Twin factory peak roofs (sawtooth factory style!)
        const roofMat = new THREE.MeshStandardMaterial({ color: 0x37474f, roughness: 0.6 });
        for (let i = 0; i < 2; i++) {
          const rx = -0.18 + i * 0.36;
          const peakGeo = new THREE.ConeGeometry(0.24, 0.14, 4);
          const peak = new THREE.Mesh(peakGeo, roofMat);
          peak.position.set(rx, 0.36 + 0.07 + 0.04, 0.1);
          peak.rotation.y = Math.PI/4;
          peak.castShadow = true;
          group.add(peak);
        }

      } else {
        // --- High Density: Massive Logistics Center / Warehouse ---
        const warehouseGeo = new THREE.BoxGeometry(0.84, 0.45, 0.84);
        const warehouse = new THREE.Mesh(warehouseGeo, new THREE.MeshStandardMaterial({ color: 0xcfd8dc, metalness: 0.4, roughness: 0.5 }));
        warehouse.position.y = 0.225 + 0.04;
        warehouse.castShadow = true;
        warehouse.receiveShadow = true;
        group.add(warehouse);

        // Structural yellow steel trusses/trim
        const structuralTrim = new THREE.Mesh(new THREE.BoxGeometry(0.88, 0.04, 0.88), this.materials.scaffolding);
        structuralTrim.position.y = 0.45 + 0.04;
        group.add(structuralTrim);

        // Multiple roller loading docks around the sides
        const dockGeo = new THREE.BoxGeometry(0.18, 0.22, 0.02);
        const rollerMat = new THREE.MeshStandardMaterial({ color: 0x90a4ae, metalness: 0.7, roughness: 0.4 });
        
        for (let i = 0; i < 3; i++) {
          const dx = -0.24 + i * 0.24;
          const dock = new THREE.Mesh(dockGeo, rollerMat);
          dock.position.set(dx, 0.11 + 0.04, 0.42);
          group.add(dock);
        }

        // Add matching docks on the back face for complete 360° balance
        for (let i = 0; i < 3; i++) {
          const dx = -0.24 + i * 0.24;
          const dock = new THREE.Mesh(dockGeo, rollerMat);
          dock.position.set(dx, 0.11 + 0.04, -0.42);
          group.add(dock);
        }
      }
    } 
    else if (zone === 'civic') {
      // Different Civics based on variant: 0:School, 1:Fire, 2:Police, 3:Hospital, 4:TownHall
      const civicType = assetVariant % 5;
      
      if (civicType === 0) {
        // --- School: Red-brick school with playground bells ---
        const schoolGeo = new THREE.BoxGeometry(0.8, 0.35, 0.7);
        const school = new THREE.Mesh(schoolGeo, this.materials.civicBrick);
        school.position.y = 0.175 + 0.04;
        school.castShadow = true;
        school.receiveShadow = true;
        group.add(school);

        const bellTower = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.24, 0.18), this.materials.civicBrick);
        bellTower.position.set(0, 0.35 + 0.12 + 0.04, 0);
        bellTower.castShadow = true;
        group.add(bellTower);

        const bellRoof = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.12, 4), this.materials.buildingRoof);
        bellRoof.position.set(0, 0.35 + 0.24 + 0.06 + 0.04, 0);
        bellRoof.rotation.y = Math.PI/4;
        bellRoof.castShadow = true;
        group.add(bellRoof);
      } 
      else if (civicType === 1) {
        // --- Fire Station: Red garage doors, elegant grey brick tower ---
        const fireStation = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.38, 0.72), this.materials.civicRed);
        fireStation.position.y = 0.19 + 0.04;
        fireStation.castShadow = true;
        fireStation.receiveShadow = true;
        group.add(fireStation);

        // Tall hose-drying spire (essential detail)
        const spire = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.7, 0.18), this.materials.civicWhite);
        spire.position.set(-0.28, 0.35 + 0.04, -0.24);
        spire.castShadow = true;
        group.add(spire);

        const spireRoof = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.12, 4), this.materials.civicRed);
        spireRoof.position.set(-0.28, 0.7 + 0.06 + 0.04, -0.24);
        spireRoof.rotation.y = Math.PI/4;
        spireRoof.castShadow = true;
        group.add(spireRoof);

        // Huge red bay doors on front
        const doorMat = new THREE.MeshStandardMaterial({ color: 0xd32f2f, roughness: 0.5 });
        const makeDoor = (dx: number) => {
          const door = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.26, 0.02), doorMat);
          door.position.set(dx, 0.13 + 0.04, 0.36);
          group.add(door);
        };
        makeDoor(-0.16);
        makeDoor(0.16);
      } 
      else if (civicType === 2) {
        // --- Police Station: Sleek blue brick with a tiny warning beacon ---
        const policeStation = new THREE.Mesh(new THREE.BoxGeometry(0.78, 0.42, 0.7), this.materials.civicBlue);
        policeStation.position.y = 0.21 + 0.04;
        policeStation.castShadow = true;
        policeStation.receiveShadow = true;
        group.add(policeStation);

        // Side entrance details
        const entrance = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.25, 0.05), this.materials.civicWhite);
        entrance.position.set(0, 0.125 + 0.04, 0.35);
        entrance.castShadow = true;
        group.add(entrance);

        // Rotating blue beacon dome on top
        const beaconGeo = new THREE.SphereGeometry(0.04, 8, 8);
        const beaconMat = new THREE.MeshBasicMaterial({ color: 0x2979ff }); // Neon blue
        const beacon = new THREE.Mesh(beaconGeo, beaconMat);
        beacon.position.set(0, 0.42 + 0.04 + 0.04, 0);
        group.add(beacon);
      } 
      else if (civicType === 3) {
        // --- Hospital: White medical block with red cross accents on all faces ---
        const hospital = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.55, 0.8), this.materials.civicWhite);
        hospital.position.y = 0.275 + 0.04;
        hospital.castShadow = true;
        hospital.receiveShadow = true;
        group.add(hospital);

        // Multi-level wing
        const wing = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.75, 0.45), this.materials.civicWhite);
        wing.position.set(-0.16, 0.375 + 0.04, -0.16);
        wing.castShadow = true;
        group.add(wing);

        // Red cross indicators on the faces (360° view)
        const makeCross = (cx: number, cy: number, cz: number, ry = 0) => {
          const crossGroup = new THREE.Group();
          crossGroup.position.set(cx, cy, cz);
          crossGroup.rotation.y = ry;

          const barH = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.04, 0.01), this.materials.civicRed);
          const barV = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.16, 0.01), this.materials.civicRed);
          crossGroup.add(barH, barV);
          group.add(crossGroup);
        };

        makeCross(0, 0.4, 0.41);         // front
        makeCross(0.41, 0.4, 0, Math.PI/2); // right
        makeCross(-0.41, 0.4, 0, Math.PI/2); // left
        makeCross(0, 0.4, -0.41);        // back
      } 
      else {
        // --- Town Hall: Grand neoclassical columns and pediment ---
        const hall = new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.45, 0.72), this.materials.civicWhite);
        hall.position.y = 0.225 + 0.04;
        hall.castShadow = true;
        hall.receiveShadow = true;
        group.add(hall);

        // Central Neoclassical Pediment (triangle roof)
        const triangleGeo = new THREE.ConeGeometry(0.35, 0.16, 4);
        const triangle = new THREE.Mesh(triangleGeo, this.materials.buildingRoof);
        triangle.position.set(0, 0.45 + 0.08 + 0.04, 0.1);
        triangle.rotation.y = Math.PI/4;
        triangle.castShadow = true;
        group.add(triangle);

        // Colonnade pillars in front (Z+)
        const pillarGeo = new THREE.CylinderGeometry(0.015, 0.015, 0.28, 6);
        for (let i = 0; i < 4; i++) {
          const px = -0.22 + i * 0.14;
          const pillar = new THREE.Mesh(pillarGeo, this.materials.civicWhite);
          pillar.position.set(px, 0.14 + 0.04, 0.36);
          pillar.castShadow = true;
          group.add(pillar);
        }
      }
    } 
    else if (zone === 'park') {
      // --- Park: Grassy lot with cute benches, trees, and central fountain ---
      const parkGrass = new THREE.Mesh(new THREE.BoxGeometry(0.86, 0.02, 0.86), this.materials.grass);
      parkGrass.position.y = 0.04 + 0.01;
      parkGrass.receiveShadow = true;
      group.add(parkGrass);

      // Add a mini path winding across the park
      const pathGeo = new THREE.BoxGeometry(0.82, 0.005, 0.24);
      const pathMesh = new THREE.Mesh(pathGeo, this.materials.curb);
      pathMesh.position.set(0, 0.04 + 0.011, 0);
      pathMesh.rotation.y = 0.4;
      pathMesh.receiveShadow = true;
      group.add(pathMesh);

      // Mini Pine Tree
      const tree = this.getVegetationMesh('pine');
      tree.position.set(-0.25, 0.04, -0.25);
      tree.scale.set(0.45, 0.45, 0.45);
      group.add(tree);

      // Mini benches (made of brown boxes with backs)
      const benchGroup = new THREE.Group();
      benchGroup.position.set(0.2, 0.04 + 0.01, 0.2);
      benchGroup.rotation.y = -0.6;

      const seat = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.04, 0.06), this.materials.parkBench);
      seat.position.y = 0.04;
      seat.castShadow = true;
      const backrest = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.1, 0.01), this.materials.parkBench);
      backrest.position.set(0, 0.08, -0.03);
      backrest.castShadow = true;
      benchGroup.add(seat, backrest);
      group.add(benchGroup);

      // Mini beautiful circular fountain in the center if variant is high!
      if (assetVariant % 2 === 0) {
        const poolGeo = new THREE.CylinderGeometry(0.12, 0.12, 0.04, 12);
        const pool = new THREE.Mesh(poolGeo, this.materials.curb);
        pool.position.set(0, 0.04 + 0.02, 0);
        pool.castShadow = true;
        group.add(pool);

        const waterGeo = new THREE.CylinderGeometry(0.1, 0.1, 0.01, 12);
        const fWater = new THREE.Mesh(waterGeo, this.materials.water);
        fWater.position.set(0, 0.04 + 0.04, 0);
        group.add(fWater);

        // Water jet
        const jetGeo = new THREE.CylinderGeometry(0.01, 0.015, 0.08, 6);
        const jet = new THREE.Mesh(jetGeo, this.materials.water);
        jet.position.set(0, 0.04 + 0.06, 0);
        group.add(jet);
      }
    }

    this.meshCache.set(cacheKey, group);
    return group.clone();
  }

  /**
   * Generates a 3D vehicle representation (solid body, dark wheels, illuminated lights)
   */
  public getVehicleMesh(colorHex = 0xff1744): THREE.Object3D {
    const cacheKey = `vehicle_${colorHex}`;
    if (this.meshCache.has(cacheKey)) {
      return this.meshCache.get(cacheKey)!.clone();
    }

    const group = new THREE.Group();

    // Main colored chassis
    const bodyMat = new THREE.MeshStandardMaterial({ color: colorHex, roughness: 0.3, metalness: 0.4 });
    const bodyGeo = new THREE.BoxGeometry(0.15, 0.08, 0.3);
    const bodyMesh = new THREE.Mesh(bodyGeo, bodyMat);
    bodyMesh.position.y = 0.07;
    bodyMesh.castShadow = true;
    bodyMesh.receiveShadow = true;
    group.add(bodyMesh);

    // Windshield / Cabin
    const cabinGeo = new THREE.BoxGeometry(0.13, 0.07, 0.16);
    const cabin = new THREE.Mesh(cabinGeo, this.materials.buildingGlass);
    cabin.position.set(0, 0.14, -0.02);
    cabin.castShadow = true;
    group.add(cabin);

    // 4 cylinder black wheels
    const wheelGeo = new THREE.CylinderGeometry(0.035, 0.035, 0.02, 8);
    const wheelMat = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.9 });
    
    const wheelOffsets = [
      [-0.08, 0.035, 0.08],
      [0.08, 0.035, 0.08],
      [-0.08, 0.035, -0.08],
      [0.08, 0.035, -0.08]
    ];

    wheelOffsets.forEach(([wx, wy, wz]) => {
      const wheel = new THREE.Mesh(wheelGeo, wheelMat);
      wheel.position.set(wx, wy, wz);
      wheel.rotation.z = Math.PI / 2; // turn sideways
      wheel.castShadow = true;
      group.add(wheel);
    });

    // Illuminated white headlights in front (Z+)
    const headlightGeo = new THREE.BoxGeometry(0.03, 0.02, 0.01);
    const headlightMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    
    const headlightL = new THREE.Mesh(headlightGeo, headlightMat);
    headlightL.position.set(-0.05, 0.08, 0.151);
    const headlightR = new THREE.Mesh(headlightGeo, headlightMat);
    headlightR.position.set(0.05, 0.08, 0.151);
    group.add(headlightL, headlightR);

    // Illuminated red taillights in back (Z-)
    const taillightGeo = new THREE.BoxGeometry(0.03, 0.015, 0.01);
    const taillightMat = new THREE.MeshBasicMaterial({ color: 0xff1744 });

    const taillightL = new THREE.Mesh(taillightGeo, taillightMat);
    taillightL.position.set(-0.05, 0.08, -0.151);
    const taillightR = new THREE.Mesh(taillightGeo, taillightMat);
    taillightR.position.set(0.05, 0.08, -0.151);
    group.add(taillightL, taillightR);

    // Center pivot adjust so cars stand on ground perfectly
    group.scale.set(0.9, 0.9, 0.9);

    this.meshCache.set(cacheKey, group);
    return group.clone();
  }

  /**
   * Generates a 3D block citizen
   */
  public getPedestrianMesh(): THREE.Object3D {
    const cacheKey = 'pedestrian';
    if (this.meshCache.has(cacheKey)) {
      return this.meshCache.get(cacheKey)!.clone();
    }

    const group = new THREE.Group();

    // Torso (blue/green shirt)
    const shirtMat = new THREE.MeshStandardMaterial({ color: 0x00bcd4, roughness: 0.8 });
    const torsoGeo = new THREE.BoxGeometry(0.05, 0.08, 0.05);
    const torso = new THREE.Mesh(torsoGeo, shirtMat);
    torso.position.y = 0.09;
    torso.castShadow = true;
    group.add(torso);

    // Head (flesh colored block)
    const headMat = new THREE.MeshStandardMaterial({ color: 0xffccbc, roughness: 0.9 });
    const headGeo = new THREE.BoxGeometry(0.04, 0.04, 0.04);
    const head = new THREE.Mesh(headGeo, headMat);
    head.position.y = 0.15;
    head.castShadow = true;
    group.add(head);

    // Legs (dark pants)
    const pantsMat = new THREE.MeshStandardMaterial({ color: 0x3f51b5, roughness: 0.8 });
    const legGeo = new THREE.BoxGeometry(0.02, 0.05, 0.02);
    
    const legL = new THREE.Mesh(legGeo, pantsMat);
    legL.name = 'legL';
    legL.position.set(-0.012, 0.025, 0);
    legL.castShadow = true;
    
    const legR = new THREE.Mesh(legGeo, pantsMat);
    legR.name = 'legR';
    legR.position.set(0.012, 0.025, 0);
    legR.castShadow = true;
    
    group.add(legL, legR);

    group.scale.set(0.9, 0.9, 0.9);

    this.meshCache.set(cacheKey, group);
    return group.clone();
  }
}
